import PageSEO from '../../components/feature/PageSEO';
import Footer from '../home/components/Footer';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://arvakon.com';

const standardsJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Standards & Reference Framework – Arvakon',
  url: `${siteUrl}/standards`,
  description: 'Arvakon verification activities reference ASME BPVC, ISO 3834, EN 13445, PED 2014/68/EU, ISO 12944, NORSOK M-501, EN 10204, and other internationally recognized codes and standards.',
  mainEntity: {
    '@type': 'ItemList',
    name: 'Standards Reference Framework',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'ASME BPVC (Sections II, V, VIII, IX)' },
      { '@type': 'ListItem', position: 2, name: 'PED 2014/68/EU' },
      { '@type': 'ListItem', position: 3, name: 'EN 13445' },
      { '@type': 'ListItem', position: 4, name: 'ISO 3834' },
      { '@type': 'ListItem', position: 5, name: 'ISO 12944' },
      { '@type': 'ListItem', position: 6, name: 'NORSOK M-501' },
      { '@type': 'ListItem', position: 7, name: 'EN 10204' },
    ],
  },
};

export default function StandardsPage() {
  const standardsCategories = [
    {
      title: 'PRESSURE EQUIPMENT',
      standards: [
        {
          code: 'ASME BPVC (Sections II, V, VIII, IX)',
          description: 'American Society of Mechanical Engineers Boiler and Pressure Vessel Code. Sections II (Materials), V (Nondestructive Examination), VIII (Pressure Vessels), and IX (Welding and Brazing Qualifications) provide technical requirements for pressure equipment design, fabrication, and inspection.',
        },
        {
          code: 'PED 2014/68/EU',
          description: 'European Pressure Equipment Directive establishing harmonized rules for the design, manufacture, and conformity assessment of pressure equipment and assemblies with a maximum allowable pressure greater than 0.5 bar.',
        },
        {
          code: 'EN 13445',
          description: 'European standard for unfired pressure vessels covering materials, design, fabrication, inspection, and testing requirements. Provides alternative compliance route to PED essential safety requirements.',
        },
      ],
    },
    {
      title: 'WELDING',
      standards: [
        {
          code: 'ISO 3834 (reference framework)',
          description: 'International standard defining quality requirements for fusion welding of metallic materials. Establishes comprehensive quality requirements for manufacturers engaged in welding activities, covering technical review, personnel qualifications, and production control.',
        },
        {
          code: 'ISO 15614',
          description: 'Specification and qualification of welding procedures for metallic materials. Defines requirements for welding procedure tests and establishes parameters for procedure qualification records.',
        },
        {
          code: 'ASME Section IX',
          description: 'Welding and brazing qualifications standard providing rules for qualification of welders, welding operators, brazers, and welding procedures. Establishes essential variables and qualification test requirements.',
        },
        {
          code: 'AWS D1.1',
          description: 'American Welding Society structural welding code for steel. Covers requirements for welding steel structures including qualification, fabrication, inspection, and acceptance criteria.',
        },
      ],
    },
    {
      title: 'NON-DESTRUCTIVE TESTING',
      standards: [
        {
          code: 'ASME Section V',
          description: 'Nondestructive examination standard providing requirements and methods for radiographic, ultrasonic, magnetic particle, liquid penetrant, eddy current, visual, and leak testing of materials and welds.',
        },
        {
          code: 'EN ISO 9712 (personnel qualification reference)',
          description: 'International standard for qualification and certification of non-destructive testing personnel. Defines requirements for principles, qualification, and certification of personnel involved in industrial NDT activities.',
        },
      ],
    },
    {
      title: 'PROTECTIVE COATING',
      standards: [
        {
          code: 'ISO 12944',
          description: 'International standard for corrosion protection of steel structures by protective paint systems. Covers surface preparation, coating selection, application procedures, and quality control requirements for protective coating systems.',
        },
        {
          code: 'NORSOK M-501',
          description: 'Norwegian offshore standard for surface preparation and protective coating. Defines requirements for coating systems applied to carbon steel and low-alloy steel in offshore and marine environments.',
        },
      ],
    },
    {
      title: 'MATERIAL CERTIFICATION',
      standards: [
        {
          code: 'EN 10204',
          description: 'European standard defining types of inspection documents for metallic products. Establishes requirements for inspection certificates including material composition, mechanical properties, and test results. Defines certificate types 2.1, 2.2, 3.1, and 3.2 based on validation requirements.',
        },
        {
          code: 'Traceability requirements per applicable project specification',
          description: 'Material traceability verification performed in accordance with project-specific technical requirements. Includes review of heat numbers, material test reports, chain of custody documentation, and correlation between material certifications and physical material identification markings.',
        },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <PageSEO
        title="Standards & Reference Framework – Arvakon | ASME, ISO, EN, NORSOK"
        description="Arvakon verification activities reference ASME BPVC, ISO 3834, EN 13445, PED 2014/68/EU, ISO 12944, NORSOK M-501, EN 10204, and other internationally recognized industrial codes and standards."
        keywords="ASME BPVC, ISO 3834, EN 13445, PED 2014/68/EU, ISO 12944, NORSOK M-501, EN 10204, industrial standards, NDT standards"
        canonical={`${siteUrl}/standards`}
        jsonLd={standardsJsonLd}
      />
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-8 py-5 flex items-center justify-between">
          <a href="/" className="text-dark-primary text-xl font-semibold tracking-tight">
            ARVAKON
          </a>
          <nav className="flex items-center gap-8">
            <a href="/" className="text-sm text-slate-600 hover:text-dark-primary transition-colors">
              Home
            </a>
            <a href="/services" className="text-sm text-slate-600 hover:text-dark-primary transition-colors">
              Services
            </a>
            <a href="/standards" className="text-sm text-dark-primary font-medium">
              Standards
            </a>
            <a href="/projects" className="text-sm text-slate-600 hover:text-dark-primary transition-colors">
              Industry Applications
            </a>
            <a href="/about" className="text-sm text-slate-600 hover:text-dark-primary transition-colors">
              About
            </a>
            <a href="/contact" className="text-sm text-slate-600 hover:text-dark-primary transition-colors">
              Contact
            </a>
          </nav>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-40 pb-24 px-8">
        <div className="max-w-5xl mx-auto">
          {/* Page Header */}
          <div className="mb-20">
            <h1 className="font-serif text-4xl text-dark-primary mb-6 tracking-tight">
              Standards &amp; Reference Framework
            </h1>
            <div className="space-y-4 text-slate-600 leading-7 max-w-4xl">
              <p>
                Arvakon verification activities are performed in accordance with internationally recognized codes, standards, and project-specific technical specifications.
              </p>
              <p>
                Services do not constitute certification or regulatory approval authority.
              </p>
              <p className="text-xs text-slate-400 leading-6 mt-6">
                This standards reference framework may be provided in printable format upon request for procurement and vendor qualification documentation purposes.
              </p>
              <p className="text-xs text-slate-400 leading-6">
                Framework reference: ARV-SRF-001 | Aligned with ARV-VQD-001 Rev.1 | Issue Date: 2025-03-02
              </p>
            </div>
          </div>

          {/* Standards Categories */}
          <div className="space-y-16">
            {standardsCategories.map((category, index) => (
              <div key={category.title}>
                {/* Category Header */}
                <div className="mb-8">
                  <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500 pb-3 border-b border-slate-200">
                    {category.title}
                  </h2>
                </div>

                {/* Standards List */}
                <div className="space-y-8">
                  {category.standards.map((standard) => (
                    <div key={standard.code} className="bg-slate-50 border border-slate-200 rounded-lg p-8">
                      <h3 className="text-base font-semibold text-dark-primary mb-4 tracking-tight">
                        {standard.code}
                      </h3>
                      <p className="text-sm text-slate-600 leading-7">
                        {standard.description}
                      </p>
                    </div>
                  ))}
                </div>

                {/* Divider between categories (except last) */}
                {index < standardsCategories.length - 1 && (
                  <div className="mt-16 border-t border-slate-200"></div>
                )}
              </div>
            ))}
          </div>

          {/* Disclaimer */}
          <div className="mt-20 pt-12 border-t border-slate-200">
            <div className="bg-slate-50 border-l-2 border-industrial-steel p-8 rounded-r-lg">
              <p className="text-sm text-slate-600 leading-7">
                Standards referenced above serve as technical frameworks for verification activities. Arvakon does not issue certifications, accreditations, or regulatory approvals. All verification services are performed as independent third-party technical review in accordance with contractual scope and applicable project specifications.
              </p>
            </div>
          </div>

          {/* Scope Clarifications */}
          <div className="mt-20 pt-12 border-t border-slate-200">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-12">
              Scope Clarifications
            </h2>
            <div className="space-y-0">
              {[
                {
                  q: 'Does listing a standard imply accreditation or certification authority?',
                  a: 'No. Standards listed on this page represent reference frameworks used during verification activities. Arvakon does not claim accreditation, certification authority, or regulatory approval rights under these standards.',
                },
                {
                  q: 'Are verification activities performed against specific clauses?',
                  a: 'Verification scope is defined by client-approved inspection and test plans and applicable project specifications. Activities may reference relevant clauses within applicable standards as required by contract.',
                },
                {
                  q: 'Does Arvakon issue certificates of conformity?',
                  a: 'No. Arvakon provides structured technical reporting. Certificates of conformity and regulatory approvals are issued by designated certification bodies or responsible authorities.',
                },
                {
                  q: 'Can standards be adapted to project-specific requirements?',
                  a: 'Yes. Verification activities are aligned with contractual specifications, regulatory frameworks, and client-defined scope parameters.',
                },
              ].map((item, index, arr) => (
                <div
                  key={index}
                  className={`grid grid-cols-2 gap-12 py-8 ${index < arr.length - 1 ? 'border-b border-slate-200' : ''}`}
                >
                  <p className="text-sm font-medium text-dark-primary leading-7">
                    {item.q}
                  </p>
                  <p className="text-sm text-slate-600 leading-7">
                    {item.a}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Governance & Independence */}
          <div className="mt-16 pt-10 border-t border-slate-200">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-6">
              Governance &amp; Independence
            </h2>
            <div className="space-y-4 max-w-4xl">
              <p className="text-sm text-slate-600 leading-7">
                Arvakon operates under a structured governance framework ensuring technical impartiality and operational independence across all verification activities.
              </p>
              <p className="text-sm text-slate-600 leading-7">
                For detailed governance principles and independence structure, please refer to the{' '}
                <a href="/about" className="text-slate-700 underline underline-offset-2 hover:text-dark-primary transition-colors">
                  About Arvakon
                </a>{' '}
                page.
              </p>
            </div>
          </div>

          {/* Procurement Documentation */}
          <div className="mt-16 pt-10 border-t border-slate-200">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-6">
              Procurement Documentation
            </h2>
            <div className="space-y-4 max-w-4xl">
              <p className="text-sm text-slate-600 leading-7">
                Structured documentation summaries related to industry application scope may be provided upon formal request for procurement and vendor qualification processes.
              </p>
              <p className="text-sm text-slate-600 leading-7">
                For documentation requests, please use the{' '}
                <a href="/contact" className="text-slate-700 underline underline-offset-2 hover:text-dark-primary transition-colors">
                  Technical Inquiry
                </a>{' '}
                form.
              </p>
              <p className="text-xs text-slate-400 leading-7">
                Documentation reference: ARV-VQD-001 Rev.1 | Issue Date: 2025-03-02
              </p>
            </div>
          </div>

        </div>
      </main>

      <Footer />
    </div>
  );
}